import { EllipsisHorizontalIcon } from '@heroicons/react/24/outline';
import { PinIcon as PinSolidIcon } from '@sidekickicons/react/24/solid';
import { getModuleTextColor } from '@/modules/shared/colors';
import { useLongPress } from '@/common/hooks/useLongPress';
import { DEFAULT_MODULE_ICONS } from './constants';
import type { SidebarModuleItemProps } from './types';

export function SidebarModuleItemRow({
  item,
  isActive,
  isActionsMenuOpen,
  isReadOnly,
  isMobile,
  onClick,
  onLongPress,
  onContextMenu,
  onActionsClick,
  draggable,
  onDragStart,
  onDragOver,
  onDragEnd,
  renamingModuleId,
  editingModuleNameRefs,
  onSaveModuleName,
  onModuleNameBlur,
  onModuleNameKeyDown,
}: SidebarModuleItemProps) {
  const isPinnedModule = Boolean(item.preference?.pinned);
  const isRenaming = renamingModuleId === item.module.id;
  const config = (item.module.config ?? {}) as Record<string, unknown>;
  const highlightColor = (config.highlightColor as string) || null;
  const customIcon = (config.icon as string) || null;
  const displayIcon = customIcon || DEFAULT_MODULE_ICONS[item.module.type] || '📦';
  const textColor = getModuleTextColor(highlightColor);

  const longPressHandlers = useLongPress({
    onLongPress: () => onLongPress(item),
    onClick: () => onClick(item),
    enabled: isMobile && !isReadOnly,
  });

  return (
    <div
      className={`conversation-item sidebar-module-item${isActive ? ' active' : ''}${isActionsMenuOpen ? ' actions-open' : ''}${isRenaming ? ' renaming' : ''}`}
      onClick={!isMobile && !isRenaming ? () => onClick(item) : undefined}
      {...(isMobile && !isRenaming ? longPressHandlers : {})}
      onContextMenu={(event) => onContextMenu(event, item)}
      role="button"
      tabIndex={0}
      draggable={draggable && !isRenaming}
      onDragStart={(e) => onDragStart?.(e, item)}
      onDragOver={(e) => onDragOver?.(e, item)}
      onDragEnd={onDragEnd}
      onKeyDown={(event) => {
        if (!isRenaming && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          onClick(item);
        }
      }}
      aria-label={`Open module ${item.displayName}`}
    >
      {!isReadOnly && !isMobile && (
        <span className="module-drag-grip" aria-hidden="true">⠿</span>
      )}
      <span className={`module-sidebar-icon ${customIcon ? '' : 'default'}`} style={textColor ? { color: textColor, opacity: 1 } : undefined}>{displayIcon}</span>
      <div className={`conversation-info${isRenaming ? ' renaming' : ''}`}>
        <span
          ref={editingModuleNameRefs ? (el) => { editingModuleNameRefs.current[item.module.id] = el; } : undefined}
          className={`conversation-name${isRenaming ? ' renaming' : ''}`}
          contentEditable={isRenaming}
          suppressContentEditableWarning
          spellCheck={false}
          style={textColor ? { color: textColor, fontWeight: 500 } : undefined}
          onClick={isRenaming ? (e) => e.stopPropagation() : undefined}
          onBlur={(e) => {
            if (!isRenaming) return;
            // Prefer the guarded blur handler: it skips the trailing blur after
            // an Enter/Escape commit so the same label isn't PATCHed twice.
            // Fall back to the direct save for consumers that don't wire it. (#3598)
            if (onModuleNameBlur) onModuleNameBlur(item, e.currentTarget);
            else onSaveModuleName?.(item, e.currentTarget);
          }}
          onKeyDown={(e) => isRenaming && onModuleNameKeyDown?.(e, item)}
        >
          {item.displayName}
        </span>
      </div>
      {isPinnedModule && (
        <span className="conversation-pinned-indicator" aria-hidden="true">
          <PinSolidIcon className="conversation-pinned-icon" />
        </span>
      )}
      {!isReadOnly && !isMobile && (
        <button
          className={`conversation-actions-btn ${isActionsMenuOpen ? 'open' : ''}`}
          onClick={(event) => onActionsClick(event, item)}
          title="Module actions"
          aria-label={`Module actions for ${item.displayName}`}
        >
          <EllipsisHorizontalIcon className="conversation-actions-icon" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
